/**
 * Where this device keeps its answer to "may FullStory record you".
 *
 * Versioned so that a change to what we ask — a new recorder, a policy that says something
 * different — can move to `.v2` and ask again rather than inherit a yes given to another question.
 *
 * Deliberately outside every namespace `Session.logout()` clears. The answer belongs to the device,
 * not to the account: someone who declined has declined for the next sign-in on this device too,
 * and signing out must not quietly turn that back into "not asked". Do not move it under
 * `attorneyhub.proposals.`.
 */
export const SESSION_RECORDING_CONSENT_KEY = "attorneyhub.sessionRecordingConsent.v1";

export type SessionRecordingConsent = "granted" | "declined";

type StorageLike = Pick<Storage, "getItem" | "setItem">;

/**
 * The stored answer, or `undefined` when there is none.
 *
 * Read synchronously at bootstrap, before FullStory is initialised, so that a stored yes starts
 * capture and a stored no never does — with no moment where capture starts and is then stopped.
 *
 * Anything other than a value this module wrote reads as no answer. So does storage the browser
 * refuses (a private window, blocked site data): an unreadable answer is never a yes.
 */
export function loadSessionRecordingConsent(
  storage = getBrowserLocalStorage(),
): SessionRecordingConsent | undefined {
  if (!storage) return undefined;

  try {
    const stored = storage.getItem(SESSION_RECORDING_CONSENT_KEY);
    return stored === "granted" || stored === "declined" ? stored : undefined;
  } catch {
    return undefined;
  }
}

export function saveSessionRecordingConsent(
  consent: SessionRecordingConsent,
  storage = getBrowserLocalStorage(),
): void {
  if (!storage) return;

  try {
    storage.setItem(SESSION_RECORDING_CONSENT_KEY, consent);
  } catch (error) {
    // The answer still holds for this page, it just is not remembered: the next visit asks again,
    // and with nothing stored, capture does not start. Warn, because this is the only trace of why.
    console.warn("Could not remember the session recording decision.", error);
  }
}

function getBrowserLocalStorage(): StorageLike | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}
